import type { SimulationState, GameSpeed } from '../core/types';
import { HUD, type HUDAction } from './hud/HUD';

/**
 * UIManager: owns the HTML overlay, navigation stack, and all UI screens.
 * Mounts into the #ui-overlay div.
 */
export class UIManager {
  private overlay: HTMLElement;
  private hud: HUD;
  private panelContainer: HTMLDivElement;
  private navigationStack: string[] = [];

  constructor(
    overlayElement: HTMLElement,
    onSpeedChange?: (speed: GameSpeed) => void,
    onAction?: (action: HUDAction) => void,
  ) {
    this.overlay = overlayElement;
    this.overlay.classList.add('game-ui');

    // HUD at the top
    this.hud = new HUD(onSpeedChange, onAction);
    this.hud.mount(this.overlay);

    // Panel area below HUD
    this.panelContainer = document.createElement('div');
    this.panelContainer.style.cssText = 'position:absolute;top:40px;right:0;bottom:0;pointer-events:none;';
    this.overlay.appendChild(this.panelContainer);
  }

  /** Get the overlay element for mounting dialogs. */
  get overlayElement(): HTMLElement {
    return this.overlay;
  }

  /** Set callback for when player clicks Ship on a ready project in the HUD. */
  set onShipProject(fn: ((projectId: string) => void) | undefined) {
    this.hud.onShipProject = fn;
  }

  /** Set callback for when player clicks a project in the HUD. */
  set onProjectClick(fn: ((projectId: string) => void) | undefined) {
    this.hud.onProjectClick = fn;
  }

  /** Update all UI from simulation state. */
  update(state: SimulationState): void {
    this.hud.update(state);
  }

  pushScreen(screenId: string): void {
    this.navigationStack.push(screenId);
  }

  popScreen(): string | null {
    return this.navigationStack.pop() ?? null;
  }

  get currentScreen(): string | null {
    return this.navigationStack[this.navigationStack.length - 1] ?? null;
  }

  handleBack(): boolean {
    if (this.navigationStack.length > 0) {
      this.popScreen();
      return true;
    }
    return false;
  }

  destroy(): void {
    this.hud.destroy();
    this.panelContainer.remove();
  }
}
